import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Paper, Stack, IconButton, InputBase, Divider } from "@mui/material";
import { Check, InfoOutlined, Search } from "@mui/icons-material";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { LoadScriptNext } from "@react-google-maps/api";

import SliderRadius from "../atoms/SliderRadius";
import MapCoverage from "./MapCoverage";
import InputField from "../atoms/InputField";
import { colors } from "../../../ui/colors";
import { useModals } from "../../../../context/ModalContext";

const orangeBase = colors.orange.base;

const presetColors = [orangeBase, "#00bcd4", "#e91e63", "#8b5cf6", "#10b981", "#3b82f6"];

const DEFAULT_COORDS = { lat: 40.7128, lng: -74.006 };
const DEFAULT_GEO = { address: "", city: "", state: "", zipCode: "", country: "" };

interface AddCoverageProps {
  onSave?: (data: any) => void;
  initialData?: any;
  onCancelEdit?: () => void;
}

export const AddCoverage: React.FC<AddCoverageProps> = ({ initialData, onSave, onCancelEdit }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState(false);
  const [radius, setRadius] = useState(5);
  const [coords, setCoords] = useState(DEFAULT_COORDS);
  const [selectedColor, setSelectedColor] = useState(initialData?.color || orangeBase);
  const [geoDetails, setGeoDetails] = useState({
    address: initialData?.address || "",
    city: initialData?.city || "",
    state: initialData?.state || "",
    zipCode: initialData?.zipCode || "",
    country: initialData?.country || "",
  });

  const [scriptLoaded, setScriptLoaded] = useState(false);

  const { closeByKey } = useModals();
  const isEditing = Boolean(initialData?.id);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || "");
      setCoords({ lat: initialData.latitude || DEFAULT_COORDS.lat, lng: initialData.longitude || DEFAULT_COORDS.lng });
      setRadius(initialData.milesOfCoverageArea || 5);
      setSelectedColor(initialData.color || orangeBase);
      setGeoDetails({
        address: initialData.address || "",
        city: initialData.city || "",
        state: initialData.state || "",
        zipCode: initialData.zipCode || "",
        country: initialData.country || "",
      });
    } else {
      setName("");
      setCoords(DEFAULT_COORDS);
      setRadius(5);
      setSelectedColor(orangeBase);
      setError(false);
      setGeoDetails(DEFAULT_GEO);
    }
  }, [initialData]);

  const previewArea = useMemo(
    () => ({
      ...initialData,
      name,
      latitude: coords.lat,
      longitude: coords.lng,
      milesOfCoverageArea: radius,
      color: selectedColor,
    }),
    [initialData, name, coords.lat, coords.lng, radius, selectedColor],
  );

  const formattedArea = (Math.PI * radius * radius).toLocaleString(undefined, { maximumFractionDigits: 1 });

  const handleCancel = () => {
    setName("");
    setRadius(5);
    setCoords(DEFAULT_COORDS);
    setSelectedColor(orangeBase);
    setError(false);
    onCancelEdit?.();
    closeByKey("Organization-CoverageAreas");
  };

  const handleSaveClick = () => {
    if (!name.trim() || !geoDetails.address) {
      setError(true);
      return;
    }

    onSave?.({
      id: initialData?.id || undefined,
      name,
      color: selectedColor || orangeBase,
      milesOfCoverageArea: Number(radius),
      latitude: coords.lat,
      longitude: coords.lng,
      address: geoDetails.address,
      city: geoDetails.city,
      state: geoDetails.state,
      zipCode: geoDetails.zipCode,
      country: geoDetails.country,
    });

    handleCancel();
    closeByKey("Organization-CoverageAreas");
  };

  const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleSelect = async (address: string) => {
    setError(false);

    try {
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      setCoords(latLng);

      const components = results[0].address_components;
      const getField = (type: string) => components.find((c) => c.types.includes(type))?.long_name || "";

      setGeoDetails({
        address: results[0].formatted_address,
        city: getField("locality") || getField("sublocality"),
        state: getField("administrative_area_level_1"),
        zipCode: getField("postal_code"),
        country: getField("country"),
      });
    } catch (e) {
      console.error("Geocoding error:", e);
      setError(true);
    }
  };

  return (
    <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""} libraries={["places"]} onLoad={() => setScriptLoaded(true)}>
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5, height: "100%" }}>
        <Box>
          <InputField
            label="Alias"
            value={name}
            placeholder="Enter a name for this zone..."
            onChange={(val) => {
              setName(val);
              if (val.trim()) setError(false);
            }}
            width="100%"
          />
          {error && <Typography sx={{ color: "#ef4444", fontSize: "11px", mt: 0.5, fontWeight: 500 }}>Please write a name before saving.</Typography>}
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "var(--fx-surface-textsecondary, #64748B)" }}>
            Search Location
          </Typography>
          <PlacesAutocomplete
            value={geoDetails.address}
            onChange={(val) => {
              setGeoDetails((prev) => ({ ...prev, address: val }));
              if (val.trim()) setError(false);
            }}
            onSelect={handleSelect}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <Box sx={{ position: "relative" }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: "2px 8px",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "8px",
                    border: `1px solid ${error ? "#ef4444" : "var(--fx-surface-border, #E2E8F0)"}`,
                    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                    "&:focus-within": { borderColor: error ? "#ef4444" : orangeBase, bgcolor: "var(--fx-surface-base, #FFFFFF)" },
                  }}
                >
                  <InputBase {...getInputProps({ placeholder: "Search for a city, street or place..." })} sx={{ ml: 1, flex: 1, fontSize: "13px" }} />
                  <Divider sx={{ height: 20, m: 0.5 }} orientation="vertical" />
                  <IconButton size="small" sx={{ p: "5px", color: orangeBase }}>
                    <Search sx={{ fontSize: 18 }} />
                  </IconButton>
                </Paper>

                {error && (
                  <Typography sx={{ color: "#ef4444", fontSize: "11px", mt: 0.5, fontWeight: 500 }}>
                    Please search and select a location before saving.
                  </Typography>
                )}

                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 2000,
                    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    maxHeight: "200px",
                    overflowY: "auto",
                    mt: 0.5,
                  }}
                >
                  {loading && <Box sx={{ p: 1, fontSize: 12, color: "gray" }}>Loading...</Box>}
                  {suggestions.map((suggestion, idx) => (
                    <Box
                      key={idx}
                      {...getSuggestionItemProps(suggestion, {
                        style: {
                          padding: "10px",
                          cursor: "pointer",
                          fontSize: "12px",
                          backgroundColor: suggestion.active ? "var(--fx-colors-warning-50, #FFF7ED)" : "var(--fx-surface-base, #FFFFFF)",
                          borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
                        },
                      })}
                    >
                      {suggestion.description}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </PlacesAutocomplete>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "var(--fx-surface-textsecondary, #64748B)" }}>
            Set Center Point
          </Typography>
          <Paper
            variant="outlined"
            sx={{ height: 180, minHeight: 180, width: "100%", position: "relative", overflow: "hidden", borderRadius: "12px" }}
          >
            {scriptLoaded && <MapCoverage key={`map-${coords.lat}-${coords.lng}-${radius}-${selectedColor}`} areas={previewArea} center={coords} />}
          </Paper>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "var(--fx-surface-textsecondary, #64748B)" }}>
            Zone Color
          </Typography>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ width: "100%", py: 1 }}>
            {presetColors.map((color) => (
              <Box
                key={color}
                onClick={() => setSelectedColor(color)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: color,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: selectedColor === color ? "2px solid var(--fx-surface-textsecondary, #64748B)" : "1px solid var(--fx-surface-border, #E2E8F0)",
                  transition: "all 0.2s",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              >
                {selectedColor === color && <Check sx={{ color: "white", fontSize: 16 }} />}
              </Box>
            ))}

            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: !presetColors.includes(selectedColor) ? selectedColor : "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                position: "relative",
                border: !presetColors.includes(selectedColor) ? "2px solid var(--fx-surface-textsecondary, #64748B)" : "1px solid var(--fx-surface-border, #E2E8F0)",
                boxShadow: !presetColors.includes(selectedColor) ? `0 0 8px ${selectedColor}60` : "none",
                transition: "all 0.3s ease",
                "&:hover": { transform: "scale(1.1)" },
              }}
            >
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
              />
              {!presetColors.includes(selectedColor) && <Check sx={{ color: "white", fontSize: 16 }} />}
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" sx={{ width: "full", alignItems: "center", justifyContent: "space-between" }}>
          <InputField
            label="LATITUDE"
            type="number"
            value={coords.lat}
            inputProps={{ step: 0.000001 }}
            onKeyDown={handleNumericKeyDown}
            onChange={(val) => setCoords({ ...coords, lat: Number(val) })}
            width="48%"
          />
          <InputField
            label="LONGITUDE"
            type="number"
            value={coords.lng}
            inputProps={{ step: 0.000001 }}
            onKeyDown={handleNumericKeyDown}
            onChange={(val) => setCoords({ ...coords, lng: Number(val) })}
            width="48%"
          />
        </Stack>

        <Box>
          <SliderRadius color={selectedColor} value={radius} step={0.1} min={0.1} max={20} onChange={setRadius} />
        </Box>

        <Box sx={{ p: 1.5, bgcolor: "var(--fx-surface-sunken, #F8FAFC)", borderRadius: "10px", border: "1px solid #ffedd5", display: "flex", gap: 1.5 }}>
          <InfoOutlined sx={{ color: orangeBase, fontSize: 20, mt: 0.3 }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#9a3412", display: "block" }}>
              Estimated Coverage
            </Typography>
            <Typography variant="caption" sx={{ color: "#c2410c", lineHeight: 1.2 }}>
              Based on the current radius, this zone will cover approximately <strong>{formattedArea} mi².</strong>
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button fullWidth onClick={handleCancel} sx={{ textTransform: "none", color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveClick}
            sx={{
              bgcolor: orangeBase,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { bgcolor: "#ea580c", boxShadow: "none" },
            }}
          >
            {isEditing ? "Update coverage area" : "Save coverage area"}
          </Button>
        </Stack>
      </Box>
    </LoadScriptNext>
  );
};

export default AddCoverage;
